import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const Header = () => {
  const { t, lang, switchLang } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const base = `/${lang}`;

  const navItems = [
    { key: "home", to: `${base}`, end: true, label: t("header.menu.home") },
    { key: "services", to: `${base}#services`, label: t("header.menu.services") },
    { key: "about", to: `${base}#about`, label: t("header.menu.aboutUs") },
    { key: "resources", to: `${base}#resources`, label: t("header.menu.resources") },
    { key: "contact", to: `${base}#contact`, label: t("header.menu.contact") },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm font-medium tracking-wide ${
      isActive ? "text-white" : "text-white/80 hover:text-white"
    }`;

  const close = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full border-b border-[#0b224a] bg-[#0F2C59] text-white"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 lg:px-8">
        <NavLink
          to={base}
          data-testid="header-brand-link"
          className="flex items-center gap-3"
          onClick={close}
        >
          <span
            aria-hidden="true"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-[#D4AF37]/60 bg-[#0b224a] text-[#D4AF37]"
          >
            <span className="font-serif text-lg font-semibold">K</span>
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-lg font-semibold text-white">
              {t("header.brandName")}
            </span>
            <span className="text-xs uppercase tracking-widest text-white/60">
              {t("header.brandTagline")}
            </span>
          </span>
        </NavLink>

        <nav
          aria-label="primary"
          className="hidden items-center gap-8 lg:flex"
          data-testid="desktop-nav"
        >
          {navItems.map((item) => {
            if (item.key === "home") {
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  end
                  className={linkClass}
                  data-testid={`nav-${item.key}`}
                >
                  {item.label}
                </NavLink>
              );
            }
            return (
              <a
                key={item.key}
                href={item.to}
                className="text-sm font-medium tracking-wide text-white/80 hover:text-white"
                data-testid={`nav-${item.key}`}
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div
            className="flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs"
            aria-label={t("header.language.label")}
            data-testid="language-switcher"
          >
            <button
              type="button"
              onClick={() => switchLang("mx")}
              className={`px-1.5 py-0.5 font-semibold ${
                lang === "mx" ? "text-[#D4AF37]" : "text-white/70 hover:text-white"
              }`}
              data-testid="language-switcher-es"
              aria-pressed={lang === "mx"}
            >
              {t("header.language.es")}
            </button>
            <span className="text-white/30">|</span>
            <button
              type="button"
              onClick={() => switchLang("en")}
              className={`px-1.5 py-0.5 font-semibold ${
                lang === "en" ? "text-[#D4AF37]" : "text-white/70 hover:text-white"
              }`}
              data-testid="language-switcher-en"
              aria-pressed={lang === "en"}
            >
              {t("header.language.en")}
            </button>
          </div>
          <a
            href={`${base}#newsletter`}
            data-testid="header-cta"
            className="rounded-md bg-[#0D7A5F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B6B53]"
          >
            {t("header.cta")}
          </a>
        </div>

        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md p-2 text-white lg:hidden"
          aria-label={open ? t("header.closeMenu") : t("header.openMenu")}
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="border-t border-white/10 bg-[#0F2C59] lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-5">
            {navItems.map((item) => {
              if (item.key === "home") {
                return (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    end
                    onClick={close}
                    className="text-base font-medium text-white/90"
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    {item.label}
                  </NavLink>
                );
              }
              return (
                <a
                  key={item.key}
                  href={item.to}
                  onClick={close}
                  className="text-base font-medium text-white/90"
                  data-testid={`mobile-nav-${item.key}`}
                >
                  {item.label}
                </a>
              );
            })}
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  switchLang("mx");
                  close();
                }}
                className={`rounded-full border border-white/30 px-3 py-1 text-xs font-semibold ${
                  lang === "mx" ? "bg-[#D4AF37] text-[#0F2C59]" : "text-white"
                }`}
                data-testid="mobile-language-switcher-es"
              >
                {t("header.language.es")}
              </button>
              <button
                type="button"
                onClick={() => {
                  switchLang("en");
                  close();
                }}
                className={`rounded-full border border-white/30 px-3 py-1 text-xs font-semibold ${
                  lang === "en" ? "bg-[#D4AF37] text-[#0F2C59]" : "text-white"
                }`}
                data-testid="mobile-language-switcher-en"
              >
                {t("header.language.en")}
              </button>
            </div>
            <a
              href={`${base}#newsletter`}
              onClick={close}
              className="mt-2 inline-flex items-center justify-center rounded-md bg-[#0D7A5F] px-4 py-2 text-sm font-semibold text-white"
              data-testid="mobile-header-cta"
            >
              {t("header.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
